// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { Action } from "redux";
import { ActionTypes } from "./actionTypes";
import {
    ISaveAppSettingsAction,
    IEnsureSecurityTokenAction,
} from "./applicationActions";
import {
    ILoadConnectionAction,
    ISaveConnectionAction,
    IDeleteConnectionAction,
} from "./connectionActions";
import {
    ILoadProjectAction,
    ICloseProjectAction,
    ISaveProjectAction,
    ILoadProjectAssetsAction,
    ISaveAssetMetadataAction,
    ILoadAssetMetadataAction,
    IDeleteProjectAction,
    ILoadBlobFilesAction,
    IUpdateProjectTagsFromFilesAction,
    IUploadDocsData,
    ILoadOcrContentAction,
    IUpdateAssetWithBlobAction,
    IClearALlOcrContentsExistingModel,
    ILoadTestFilesAction,
    IClearALlExistingTestFiles,
    IUpdateAssetStateAction,
    IDeleteAssetAction,
    IUpdateAssetWithImageUriStateAction,
    IUpdateTagDocumentCount,
    IDeleteProjectAssetAction,
    IProjectUpdateSuccessAction,
    IProjectUpdateTrainSuccessAction,
    ILoadOPTestFilesAction,
    IClearOPTestFilesAction,
    IAddAssetToProjectAction,
    
} from "./projectActions";
import {
    IShowAppErrorAction,
    IClearErrorAction,
} from "./appErrorActions";
import { ISetTitleAction } from "./appTitleActions";
import { ILoadUserModelsAction, IDeleteModelProjectAction, IDeleteUserModelAction } from "./modalActions";
import {
    IAuthAction,
    ILogoutAction
} from "./authenticationActions";
import {
    ILoadUserQueuesAction
} from "./operationsActions";
import { IUploadFilesCount, IUploadedFilesStatus,
    IClearUploadFilesCount, IUploadingFilesStatus } from "./filesUploadingAction";
/**
 * Data payload dispatched from the action and delivered to reducer
 */
export interface IPayloadAction<TType, TPayload> extends Action<TType> {
    payload: TPayload;
}

/**
 * Creates action and validates type of action type name
 * @param type Name for action being created
 */
// tslint:disable-next-line:max-line-length
export function createAction<TAction extends Action<TAction["type"]>>(type: TAction["type"]): () => Action<TAction["type"]> {
    return () => ({
        type,
    });
}

/**
 * Create action with payload
 * @param type Name for action being created
 */
// tslint:disable-next-line:max-line-length
export function createPayloadAction<TAction extends IPayloadAction<TAction["type"], TAction["payload"]>>(type: TAction["type"]): (payload: TAction["payload"]) => IPayloadAction<TAction["type"], TAction["payload"]> {
    return (payload: TAction["payload"]) => ({
        type,
        payload,
    });
}

/**
 * Catch-all for unregistered actions
 */
export interface IOtherAction extends Action<string> {
    type: ActionTypes.ANY_OTHER_ACTION;
}

/**
 * Helper instance of catch-all
 */
export const anyOtherAction = createAction<IOtherAction>(ActionTypes.ANY_OTHER_ACTION);

/**
 * Used by reducers to type-check all actions
 */
export type AnyAction = IOtherAction |
    ISaveAppSettingsAction |
    IEnsureSecurityTokenAction |
    ISaveConnectionAction |
    IDeleteConnectionAction |
    ILoadConnectionAction |
    ISaveConnectionAction |
    IDeleteConnectionAction |
    ILoadProjectAction |
    ICloseProjectAction |
    ISaveProjectAction |
    IDeleteProjectAction |
    ILoadProjectAssetsAction |
    ISaveAssetMetadataAction |
    ILoadAssetMetadataAction |
    IShowAppErrorAction |
    IClearErrorAction |
    IUpdateProjectTagsFromFilesAction |
    ISetTitleAction |
    ILoadBlobFilesAction |
    IUploadDocsData |
    /** model Actions */
    ILoadUserModelsAction |
    IDeleteModelProjectAction |
    IDeleteUserModelAction |
    IAuthAction |
    ILogoutAction |
    /** batch process */
    ILoadUserQueuesAction |
    /** Load Ocr Content */
    ILoadOcrContentAction |
    IUpdateAssetWithBlobAction |
    IUploadedFilesStatus |
    IUploadingFilesStatus |
    ILoadTestFilesAction |
    IClearALlExistingTestFiles |
    IUpdateAssetStateAction |
    IUpdateAssetWithImageUriStateAction |
    IUploadFilesCount |
    IClearUploadFilesCount |
    IDeleteAssetAction |
    IClearALlOcrContentsExistingModel|
    IUpdateTagDocumentCount|
    IDeleteProjectAssetAction|
    IProjectUpdateSuccessAction|
    IProjectUpdateTrainSuccessAction|
    ILoadOPTestFilesAction|
    IAddAssetToProjectAction|
    IClearOPTestFilesAction;
